import type { BaseProps } from '@/frontend/features/Fde/types'

// Forge run document, schema data-health/2: measurements and checks only.
export type CheckStatus = 'ok' | 'warn' | 'fail'
export type HealthError = 'not_readable' | 'query_failed' | null
export type Layer = 'raw' | 'stg' | 'core' | 'mart' | 'other'
export type Scalar = string | number | boolean | null
export type HealthRow = Record<string, Scalar>

export interface HealthGap { from: string; to: string; days: number }

export interface HealthPartition {
  value: string
  rows: number
  first_date: string | null
  last_date: string | null
  days_since_last: number | null
  gaps: HealthGap[]
}

export interface HealthOutliers {
  method: 'iqr'
  k?: number
  q1?: number | null
  q3?: number | null
  low?: number | null
  high?: number | null
  count: number | null
  examples?: HealthRow[]
}

export interface HealthColumn {
  column: string
  type: 'string' | 'number' | 'date'
  role?: string[]
  null_pct: number | null
  nulls?: number | null
  distinct: number | null
  min?: Scalar
  max?: Scalar
  negatives?: number | null
  out_of_range?: number | null
  outliers?: HealthOutliers | null
  // From the connector's column dictionary (optional; older runs lack them).
  label?: string | null
  description?: string | null
  unit?: string | null
  example?: Scalar
}

export interface HealthVolumeAnomaly { date: string; count: number; expected_min: number; expected_max: number; method: string }

export type KeySource = 'override' | 'table' | 'contract' | 'inferred' | null

export interface HealthTable {
  id: string
  entity: string
  subject?: string
  layer: Layer
  kind?: 'fact' | 'snapshot' | 'dimension'
  table: string
  // Friendly pt-BR name and what one row is, from the connector dictionary (optional).
  label?: string | null
  grain?: string | null
  readable: boolean
  error?: HealthError
  key?: string[]
  key_source?: KeySource
  date_column?: string | null
  partition_column?: string | null
  daily?: boolean
  rows: number | null
  raw_rows?: number | null
  first_date: string | null
  last_date: string | null
  days_since_last: number | null
  daily_counts: { start: string; counts: number[] } | null
  gaps: HealthGap[]
  partitions?: HealthPartition[]
  duplicate_keys: { key: string[]; count: number | null; blank?: number | null; inferred?: boolean; examples?: HealthRow[] } | null
  columns: HealthColumn[]
  volume_anomalies: HealthVolumeAnomaly[]
}

export interface HealthEndpoint { table: string; columns: string[] }

export interface HealthRelationship {
  id: string
  from: HealthEndpoint
  to: HealthEndpoint
  rows: number | null
  matched: number | null
  unmatched: number | null
  match_pct: number | null
  error?: HealthError
  examples?: HealthRow[]
}

export interface HealthCheck {
  id: string
  table: string
  column?: string | null
  partition?: string | null
  name?: string | null
  metric: string
  value: number | null
  threshold: number
  op: '<=' | '>='
  status: CheckStatus
  error?: HealthError
  values?: Record<string, Scalar>
  // Optional, newer runs: who defined the check, example rows (never PII) and what was measured in detail.
  source?: 'generic' | 'pack' | null
  examples?: HealthRow[] | null
  example_columns?: string[] | null
  detail?: HealthCheckDetail | null
}

export interface HealthSeries { start: string; counts: number[] }

// Measurement detail of one check, by metric. Every field is optional.
export interface HealthCheckDetail {
  series?: HealthSeries | null
  gaps?: HealthGap[] | null
  anomalies?: { date: string; count: number; expected_min: number; expected_max: number }[] | null
  partitions?: { value: string; last_date: string | null; days_since_last: number | null }[] | null
  q1?: number | null
  q3?: number | null
  low?: number | null
  high?: number | null
  top?: number[] | null
  bottom?: number[] | null
  histogram?: { lower: number; upper: number; count: number }[] | null
  keys?: HealthRow[] | null
  unmatched?: HealthRow[] | null
  codes?: { value: string; rows: number }[] | null
  allowed?: string[] | null
  min_day?: string | null
  max_day?: string | null
  before?: number | null
  after?: number | null
}

// One business subject across layers (sales, inventory...), summed by Forge.
export interface HealthSubject {
  id: string
  tables: string[]
  layers: Layer[]
  readable_layers?: Layer[]
  final_table: string | null
  final_layer?: Layer | null
  rows: number | null
  last_date: string | null
  days_since_last: number | null
  status: CheckStatus | null
  checks: Record<CheckStatus, number>
}

export interface HealthAccess {
  unreadable_tables: string[]
  reason: 'permission' | null
  skipped_checks?: number
}

export interface HealthRun {
  run_id: string
  connection_id: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  trigger?: 'post_preparation' | 'manual' | null
  started_at?: string | null
  finished_at?: string | null
  schema_version: string
  engine_version?: string
  config_sha256?: string
  as_of?: string | null
  data_until?: string | null
  error?: string | null
  summary?: {
    checks?: Record<CheckStatus, number>
    tables?: number
    unreadable_tables?: number
    queries?: number
    failed_queries?: number
  } | null
  access?: HealthAccess | null
  subjects?: HealthSubject[]
  tables?: HealthTable[]
  relationships?: HealthRelationship[]
  checks?: HealthCheck[]
}

export interface HealthRunSummary {
  run_id: string
  status: HealthRun['status']
  started_at?: string | null
  finished_at?: string | null
  trigger?: HealthRun['trigger']
  schema_version?: string
}

export interface HealthOnboarding {
  status: 'waiting_health' | 'needs_agent' | 'atlas_assigned' | 'approved' | 'bayes_assigned'
  first_run_id: string | null
  atlas_task_id: string | null
  bayes_task_id: string | null
  atlas_task_url: string | null
  bayes_task_url: string | null
  approved_at: string | null
  approved_by: string | null
  last_error: string | null
  atlas_finished: boolean
  approvable: boolean
}

export type HealthCopy = Record<string, string>

export interface DataHealthProps extends BaseProps {
  connection: { id: string; name: string; connector_name: string; icon_url?: string | null }
  run: HealthRun | null
  runs: HealthRunSummary[]
  selected_run_id: string | null
  staff: boolean
  error: string | null
  onboarding: HealthOnboarding | null
  onboarding_enabled: boolean
  health_copy: HealthCopy
  paths: { self: string; runs: string; approve: string | null; back: string }
}

// Rails i18n interpolation: %{name}.
export function format(template: string | undefined, values: Record<string, string | number> = {}) {
  return (template ?? '').replace(/%\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''))
}

export function formatDate(value: string | null | undefined, withTime = false) {
  if (!value) return ''
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR', withTime ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'short' }).format(date)
}

const integer = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const decimal = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })

export function formatNumber(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '-'
  return Number.isInteger(value) ? integer.format(value) : decimal.format(value)
}

export function formatPct(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '-'
  return `${decimal.format(value)}%`
}

// Display of a check value or threshold: metrics ending in _pct are percentages.
export function formatMetric(metric: string, value: number | null | undefined) {
  return metric.endsWith('_pct') ? formatPct(value) : formatNumber(value)
}

export function formatScalar(value: Scalar | undefined) {
  if (value == null) return '-'
  if (typeof value === 'number') return formatNumber(value)
  if (typeof value === 'boolean') return value ? 'sim' : 'não'
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : value
}
